import { useEffect, useMemo, useRef, useState } from "react";
import { ArticleSkeleton, useMinLoading } from "@/components/Preloader";
import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronDown, Crown, FileText, HelpCircle, Loader2 } from "lucide-react";
import api, { apiError, slugifyName } from "@/lib/api";
import { Article } from "@/lib/types";
import { useAuth } from "@/context/AuthContext";
import { ArticleCard } from "@/components/ArticleCard";
import { ArticleInlineAd } from "@/components/AdSlot";
import { AudioBriefPlayer } from "@/components/AudioBriefPlayer";
import { DailyBriefSignup } from "@/components/home/DailyBriefSignup";
import { KeyTakeaways } from "@/components/article/KeyTakeaways";
import { relatedHeading } from "@/components/article/ContextRail";
import { pickIntelligence } from "@/components/article/RailModules";
import { FollowTheStory } from "@/components/article/FollowTheStory";
import { ClapButton } from "@/components/ClapButton";
import { PulseStrip } from "@/components/article/standard/PulseStrip";
import { Masthead } from "@/components/article/standard/Masthead";
import { IntelStrip } from "@/components/article/standard/IntelStrip";
import { MarketContextPanel } from "@/components/article/standard/MarketContextPanel";
import { ArticleIntelPanel } from "@/components/article/standard/ArticleIntelPanel";
import { RelatedCoveragePanel } from "@/components/article/standard/RelatedCoveragePanel";
import { SponsoredRailAd } from "@/components/article/standard/SponsoredRailAd";
import { PremiumRailCard } from "@/components/article/standard/PremiumRailCard";
import { PrevNextNav } from "@/components/article/standard/PrevNextNav";
import { FeedbackRow } from "@/components/article/standard/FeedbackRow";
import { AuthorEndCard } from "@/components/article/standard/AuthorEndCard";
import { InlineAssetChart } from "@/components/article/standard/InlineAssetChart";
import { NarrationPlayer } from "@/components/article/standard/NarrationPlayer";
import { LiveTimeline } from "@/components/article/standard/LiveTimeline";
import { BodyItem, buildBodyItems, computePreviewCount, isHeadingBlock, useWideMode } from "@/components/article/standard/bodyBlocks";
import { useArticleAnalytics, trackEvent } from "@/hooks/useArticleAnalytics";
import { useReadingProgress } from "@/hooks/useReadingProgress";
import { Seo, SITE_URL, absUrl } from "@/components/Seo";
import { useLang, SUPPORTED_LANGS, articlePath } from "@/lib/i18n";
import { SECTOR_LABELS } from "@/components/layernav/navConfig";

const SCHEMA_TYPE: Record<string, string> = {
  deep_dive: "AnalysisNewsArticle", market_brief: "ReportageNewsArticle", pr: "Article", author: "Article",
};

const TRANSPARENCY: Record<string, string> = {
  imported: "NewsLayer coverage based on externally reported material.",
  pr: "Press release provided by the issuing organization. This is not NewsLayer editorial content.",
  editorial: "Original reporting and analysis by the NewsLayer editorial desk.",
  deep_dive: "NewsLayer premium research and analysis.",
  author: "Contributed by an approved NewsLayer author. Views are the author's own.",
  market_brief: "Daily market brief compiled by the NewsLayer desk.",
};

const TS_ORDER = ["sm", "md", "lg"];

/* §43-44: rail modules render exactly once — desktop rail OR mobile stack */
const useIsDesktop = () => {
  const [is, setIs] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const fn = (e: MediaQueryListEvent) => setIs(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  return is;
};

const WhyCallout = ({ text }: { text?: string }) => {
  if (!text) return null;
  return (
    <aside data-testid="why-this-matters-callout"
      className="my-8 rounded-xl border border-[#7C3AED]/25 bg-[#0d0a17] p-5">
      <p className="font-mono text-[9px] font-bold uppercase tracking-[0.28em] text-[#A78BFA]">Why this matters</p>
      <p className="mt-2 text-[15px] text-gray-300 leading-relaxed">{text}</p>
    </aside>
  );
};

export default function ArticlePage() {
  const { slug } = useParams();
  const { user, refresh } = useAuth();
  const { lang, t } = useLang();
  const queryClient = useQueryClient();
  const [textSize, setTextSize] = useState(() => localStorage.getItem("nl-text-size") || "md");
  const [narrationOpen, setNarrationOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const isDesktop = useIsDesktop();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["article", slug],
    queryFn: async () => (await api.get(`/articles/${slug}`)).data,
  });

  const { data: tx, isFetching: txLoading } = useQuery({
    queryKey: ["article-tx", slug, lang],
    queryFn: async () => (await api.get(`/articles/${slug}/translation?lang=${lang}`)).data,
    enabled: lang !== "en" && !!data?.article,
    staleTime: Infinity,
    retry: 1,
  });

  useArticleAnalytics(data?.article ? slug : undefined);
  useReadingProgress(data?.article);
  useEffect(() => { setExpanded(false); }, [slug]);

  const article: Article | undefined = data?.article;
  const useTx = lang !== "en" && !!tx;
  const dBody = (useTx && tx.body_html) || article?.body_html;
  const lv = article?.lead_visual;
  const heroImg = article ? ((article as any).hero_image_url || lv?.url || null) : null;
  const wtm = (article as any)?.why_this_matters;
  const dropCap = !!article && !article.locked && (
    ["editorial", "author", "deep_dive"].includes(article.content_type || "") ||
    ((article.read_minutes ?? 0) >= 4 && !["pr", "market_brief"].includes(article.content_type || "")));

  /* Semantic body items: hero-dupe suppression + tokenized blocks + inline modules */
  const items = useMemo<BodyItem[]>(
    () => buildBodyItems(article?.locked ? null : dBody, heroImg, dropCap),
    [dBody, heroImg, dropCap, article?.locked]);

  /* Expand Story — exempt: premium (paywall owns it), daily brief.
     Breaking/live articles still collapse: the LiveTimeline stays visible above the body. */
  const collapsible = !!article && !!dBody && !article.locked
    && article.content_type !== "market_brief";
  const previewCount = useMemo(
    () => (collapsible ? computePreviewCount(items) : items.length), [items, collapsible]);
  const collapsed = collapsible && previewCount < items.length && !expanded;

  /* Wide mode — rail-bottom aware, disabled while collapsed */
  const railInnerRef = useRef<HTMLDivElement>(null);
  const tailRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);
  const canWide = isDesktop && !!article && !article.locked && !!dBody && !collapsed;
  const { wideFrom } = useWideMode({
    enabled: canWide, railRef: railInnerRef, itemRefs, tailRef,
    itemCount: items.length, resetKey: slug || "",
  });

  const showLoader = useMinLoading(isLoading, 700, slug);
  if (showLoader) {
    return <ArticleSkeleton />;
  }
  if (isError || !article) {
    return <div className="max-w-4xl mx-auto px-4 py-24 text-center">
      <h1 className="font-heading text-2xl text-white mb-3">Story not found</h1>
      <Link to="/" className="text-[#00E5FF] hover:underline">Back to homepage</Link>
    </div>;
  }

  const saved = user?.saved_articles?.includes(article.id);
  const dTitle = (useTx && tx.title) || article.title;
  const dSummary = (useTx && (tx.summary_ai || tx.summary)) || article.summary_ai || article.summary;
  const dDek = (useTx && (tx.summary_ai || tx.summary)) || article.display_dek || null;
  const dTakeaways = (useTx && tx.key_takeaways?.length ? tx.key_takeaways : article.key_takeaways) || [];
  const dFaqs = (useTx && tx.faqs?.length ? tx.faqs : article.faqs) || [];
  const heroCredit = heroImg
    ? (lv?.tier === "B" ? "NewsLayer editorial artwork" : article.source_name ? `Image via ${article.source_name}` : null)
    : null;
  const primaryCat = article.categories?.[0];
  const seoDesc = (useTx && (tx.summary_ai || tx.summary)) || (article as any).summary_ai || article.summary || article.title;
  const seoPath = articlePath(lang, article.slug);
  const articleUrl = `${SITE_URL}${seoPath}`;
  const hreflangs = [
    { code: "x-default", href: `${SITE_URL}/article/${article.slug}` },
    { code: "en", href: `${SITE_URL}/article/${article.slug}` },
    ...SUPPORTED_LANGS.filter((l) => l !== "en")
      .map((l) => ({ code: l, href: `${SITE_URL}/${l}/article/${article.slug}` })),
  ];
  const newsJsonLd = {
    "@context": "https://schema.org",
    "@type": SCHEMA_TYPE[article.content_type || ""] || "NewsArticle",
    headline: dTitle,
    description: seoDesc,
    inLanguage: lang,
    image: [absUrl(heroImg)],
    datePublished: article.published_at,
    dateModified: (article as any).updated_at || article.published_at,
    author: [article.author_name
      ? { "@type": "Person", name: article.author_name, url: `${SITE_URL}/author/${slugifyName(article.author_name)}` }
      : { "@type": "Organization", name: "NewsLayer Desk", url: SITE_URL }],
    publisher: {
      "@type": "NewsMediaOrganization",
      name: "NewsLayer",
      logo: { "@type": "ImageObject", url: `${SITE_URL}/logo.png` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": articleUrl },
    ...(article.categories?.length ? { articleSection: article.categories[0] } : {}),
    ...((article as any).tags?.length ? { keywords: (article as any).tags.join(", ") } : {}),
  };
  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      ...(article.categories?.length
        ? [{ "@type": "ListItem", position: 2, name: article.categories[0], item: `${SITE_URL}/${article.categories[0]}` }]
        : []),
      { "@type": "ListItem", position: article.categories?.length ? 3 : 2, name: dTitle, item: articleUrl },
    ],
  };
  const faqs = article.faqs || [];
  const faqJsonLd = faqs.length ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  } : null;

  const toggleSave = async () => {
    if (!user) { toast.error("Sign in to save stories"); return; }
    try {
      if (saved) await api.delete(`/account/saved/${article.id}`);
      else await api.post(`/account/saved/${article.id}`);
      await refresh();
      queryClient.invalidateQueries({ queryKey: ["saved"] });
      trackEvent("article_save", { slug: article.slug, saved: !saved });
      toast.success(saved ? "Removed from saved" : "Saved to your account");
    } catch (e) { toast.error(apiError(e)); }
  };

  const share = async () => {
    const url = `${window.location.origin}/api/v1/s/${article.slug}`;
    trackEvent("article_share", { slug: article.slug });
    if (navigator.share) {
      try { await navigator.share({ title: article.title, url }); return; } catch (e) {}
    }
    await navigator.clipboard.writeText(url);
    toast.success("Share link copied — includes a branded preview card");
  };

  const listen = () => {
    trackEvent("article_listen", { slug: article.slug });
    if (article.audio_url) {
      document.getElementById("article-audio")?.scrollIntoView({ behavior: "smooth", block: "center" });
    } else {
      setNarrationOpen(true);
    }
  };
  const canNarrate = !article.locked && article.access_level !== "premium";

  const cycleTextSize = () => {
    const next = TS_ORDER[(TS_ORDER.indexOf(textSize) + 1) % TS_ORDER.length];
    setTextSize(next);
    localStorage.setItem("nl-text-size", next);
    trackEvent("article_text_size", { slug: article.slug, size: next });
  };
  const tsClass = textSize === "sm" ? "nl-ts-sm" : textSize === "lg" ? "nl-ts-lg" : "";

  const expandStory = () => {
    setExpanded(true);
    trackEvent("article_expand_click", { slug: article.slug });
  };

  const takeawaysBlock = dTakeaways.length > 0 && (article.read_minutes ?? 0) >= 4 && (
    <div className="mt-8" data-testid="body-key-takeaways">
      <KeyTakeaways items={dTakeaways} label={t("keyTakeaways")} />
    </div>
  );

  // Cross-module deduplication + semantic invariant (§13-21, §38):
  // specific_heading => every displayed item satisfies that heading's relevance family.
  const thread = data.story_thread || [];
  const threadSlugs: string[] = thread.map((n: { slug: string }) => n.slug);
  const railExclude = new Set<string>([...threadSlugs, ...(data.premium_pick?.slug ? [data.premium_pick.slug] : [])]);
  const relatedAll: Article[] = data.related || [];
  const strongRelated = relatedAll.filter((a: any) => a.pinned || (a.match_score ?? 0) >= 3);
  const intelItems = pickIntelligence(strongRelated, railExclude);
  const usedSlugs = new Set<string>([...railExclude, ...intelItems.map((i) => i.slug)]);
  const remaining = relatedAll.filter((a: Article) => !usedSlugs.has(a.slug));

  // Relevance > quantity: a specific heading shows 1-3 family-true cards, never padded.
  const specificLabel = relatedHeading(article.context);
  const familyPool = remaining.filter((a: any) => a.pinned || a.family_match);
  let moreStories: Article[] = [];
  let ctxHeading: string | null = null;
  if (specificLabel && familyPool.length) {
    ctxHeading = specificLabel;
    if (article.context?.subtype === "AI_SECURITY"
        && !familyPool.every((a: any) => a.pinned || a.subtype_match)) {
      ctxHeading = "More from Security";
    }
    moreStories = familyPool;
  } else {
    const cat = (article.categories || []).find((c) =>
      remaining.some((a: any) => (a.cats_true || []).includes(c)));
    if (cat) {
      ctxHeading = `More from ${SECTOR_LABELS[cat] || cat.replace(/\b\w/g, (ch) => ch.toUpperCase())}`;
      moreStories = remaining.filter((a: any) => (a.cats_true || []).includes(cat));
    } else {
      const generic = remaining.filter((a: any) => (a.match_score ?? 0) >= 1);
      if (generic.length) {
        ctxHeading = "More from NewsLayer";
        moreStories = generic;
      }
    }
  }
  if (lang !== "en" && moreStories.length) ctxHeading = t("relatedStories");
  // §24 visual diversity: skip cards whose hero image repeats an earlier pick
  const seenArt = new Set<string>();
  moreStories = moreStories.filter((a: Article) => {
    const img = a.hero_image_url || "";
    if (img && seenArt.has(img)) return false;
    if (img) seenArt.add(img);
    return true;
  }).slice(0, 3);

  const transparency = TRANSPARENCY[article.content_type || ""];
  const hasAttribution = !!(transparency || article.source_article_url);

  const railModules = (
    <>
      <MarketContextPanel article={article} />
      <ArticleIntelPanel article={article} regulationRef={data.regulation_ref} />
      <RelatedCoveragePanel items={intelItems} category={primaryCat} />
      <SponsoredRailAd />
      <PremiumRailCard />
    </>
  );

  /* Wide-mode split: blocks below the true rail bottom escape the 720px column */
  /* Wide-mode split: blocks below the true rail bottom escape the 720px column.
     Never orphan a heading at the end of the narrow column — pull it into the wide zone. */
  let effFrom = wideFrom;
  if (effFrom !== null) {
    while (effFrom > 0 && effFrom <= items.length && isHeadingBlock(items[effFrom - 1])) effFrom -= 1;
  }
  const wideActive = effFrom !== null && !collapsed;
  const visibleItems = collapsed ? items.slice(0, previewCount) : items;
  const narrowItems = wideActive ? visibleItems.slice(0, effFrom!) : visibleItems;
  const wideItems = wideActive ? visibleItems.slice(effFrom!) : [];

  const setItemRef = (i: number) => (el: HTMLDivElement | null) => { itemRefs.current[i] = el; };
  const renderItem = (it: BodyItem, i: number, wide: boolean) => {
    /* Post-rail wide region is a true one-column grid: every block spans the full
       article content width. Media keeps natural aspect (width 100%, height auto). */
    const wideCls = wide ? (it.kind === "html" && it.media ? " nl-wide-media" : "") : "";
    if (it.kind === "ad") {
      return <div key={`i${i}`} ref={setItemRef(i)} className={`nl-seg${wideCls}`}><ArticleInlineAd /></div>;
    }
    if (it.kind === "chart") {
      return <div key={`i${i}`} ref={setItemRef(i)} className={`nl-seg${wideCls}`}><InlineAssetChart article={article} /></div>;
    }
    if (it.kind === "wtm") {
      return <div key={`i${i}`} ref={setItemRef(i)} className={`nl-seg${wideCls}`}><WhyCallout text={wtm} /></div>;
    }
    return <div key={`i${i}`} ref={setItemRef(i)} className={`article-body nl-seg${wideCls}`}
      dangerouslySetInnerHTML={{ __html: it.html }} />;
  };

  /* Article-completion journey — fully hidden while the story is collapsed */
  const tailContent = (
    <>
      <div id="article-end-sentinel" data-testid="article-end-sentinel" aria-hidden className="h-px" />

      <FollowTheStory thread={thread} />

      {dFaqs.length > 0 && (
        <div className="mt-8 bg-[#121212] border border-[#7C3AED]/25 rounded-lg p-6" data-testid="article-faqs">
          <h3 className="flex items-center gap-2 font-heading font-bold text-white text-sm uppercase tracking-wide mb-4">
            <HelpCircle className="h-4 w-4 text-[#7C3AED]" /> {t("quickAnswers")}
          </h3>
          <div className="space-y-4">
            {dFaqs.map((f: { q: string; a: string }, i: number) => (
              <div key={i} data-testid={`faq-item-${i}`}>
                <p className="text-white font-semibold text-sm">{f.q}</p>
                <p className="text-gray-400 text-sm leading-relaxed mt-1">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {(article.tags || []).length > 0 && (
        <div className="flex flex-wrap gap-2 mt-8">
          {article.tags!.map((tg) => (
            <Link key={tg} to={`/search?q=${encodeURIComponent(tg)}`}
              className="font-mono text-xs text-gray-500 hover:text-[#00E5FF] transition-colors">
              #{tg}
            </Link>
          ))}
        </div>
      )}

      {/* Quiet end zone: provenance + Daily Brief (§45-48) */}
      <div data-testid="article-endcap"
        className={`mt-10 grid gap-4 items-stretch ${hasAttribution ? "md:grid-cols-[minmax(220px,28fr)_minmax(0,72fr)]" : ""}`}>
        {hasAttribution && (
          <div data-testid="source-attribution"
            className="relative overflow-hidden bg-[#0a1114] border border-[#00E5FF]/20 rounded-xl p-5">
            <span aria-hidden className="pointer-events-none absolute -bottom-12 -right-12 h-36 w-36 rounded-full bg-[#00E5FF]/[0.06] blur-2xl" />
            <p className="flex items-center gap-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.28em] text-[#00E5FF] mb-2">
              <FileText className="h-3 w-3" /> {article.content_type === "pr" ? "Disclosure" : "Sourced by"}
            </p>
            {article.source_article_url && (
              <p className="text-white font-semibold leading-snug">
                Originally reported by {article.source_name}
              </p>
            )}
            {transparency && (
              <p className="text-gray-400 text-sm leading-relaxed mt-1.5" data-testid="content-transparency">
                {transparency}
              </p>
            )}
          </div>
        )}
        <DailyBriefSignup sourceType="article_daily_brief" interests={article.categories || []}
          className="" stacked />
      </div>

      <ClapButton slug={article.slug} title={article.title} />

      <FeedbackRow slug={article.slug} />
    </>
  );

  return (
    <div data-testid="article-page">
      <Seo title={dTitle} description={seoDesc} path={seoPath}
        image={heroImg} type="article" hreflangs={hreflangs}
        jsonLd={[newsJsonLd, breadcrumbJsonLd, ...(faqJsonLd ? [faqJsonLd] : [])]} />

      {/* Pulse strip (§6): breaking headline + live market data */}
      <PulseStrip />

      <article className="max-w-[1280px] mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-x-10 xl:gap-x-14 lg:items-start">
          <div className="min-w-0">
            <Masthead article={article} title={dTitle} dek={dDek} saved={!!saved}
              onSave={toggleSave} onShare={share} hasAudio={!!article.audio_url || canNarrate} onListen={listen}
              textSize={textSize} onCycleTextSize={cycleTextSize} />

            {/* Hero media (§13) */}
            {heroImg && (
              <figure className="mt-7" data-testid="article-hero">
                <div className="relative overflow-hidden rounded-2xl border border-white/[0.08]">
                  <img src={heroImg} alt={dTitle} data-testid="article-hero-image"
                    className="w-full h-auto max-h-[560px] object-cover" fetchPriority="high" />
                </div>
                {heroCredit && (
                  <figcaption data-testid="article-hero-credit"
                    className="mt-2 text-right font-mono text-[9px] uppercase tracking-[0.18em] text-gray-600">
                    {heroCredit}
                  </figcaption>
                )}
              </figure>
            )}

            {/* Story Intelligence strip (§14) */}
            <IntelStrip article={article} />

            {/* Breaking/Developing live layer — fast-changing context above the canonical story */}
            <LiveTimeline slug={article.slug} article={article} />

            {lang !== "en" && txLoading && (
              <div data-testid="translating-indicator"
                className="mt-4 inline-flex items-center gap-2 font-mono text-xs text-[#A78BFA]">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("translating")}…
              </div>
            )}
            {article.content_type === "market_brief" && (
              <Link to="/briefs" data-testid="brief-archive-link"
                className="inline-flex items-center gap-1.5 mt-4 font-mono text-[11px] uppercase tracking-widest text-[#7C3AED] hover:text-[#00E5FF] transition-colors">
                ☀️ {t("briefArchive")} →
              </Link>
            )}

            {article.audio_url && (
              <div className="mt-5" id="article-audio">
                <AudioBriefPlayer src={article.audio_url} title={dTitle} sponsor={article.audio_sponsor} slug={article.slug} />
              </div>
            )}

            <div className={`max-w-[720px] ${tsClass}`} data-testid="article-reading-column">
              {takeawaysBlock}

              {!dBody && !article.locked && dSummary && (
                <p data-testid="article-summary"
                  className="mt-9 text-lg sm:text-xl text-gray-200 font-medium leading-relaxed border-l-2 border-[#00E5FF] pl-4">
                  {dSummary}
                </p>
              )}

              {article.locked && (
                <div className="relative mt-10" data-testid="premium-paywall">
                  {article.body_teaser && (
                    <div className="article-body pointer-events-none select-none [mask-image:linear-gradient(to_bottom,black_20%,transparent_100%)]"
                      dangerouslySetInnerHTML={{ __html: article.body_teaser }} />
                  )}
                  <div className={`relative z-10 bg-[#120b1e] border border-[#7C3AED]/40 rounded-xl p-8 text-center ${article.body_teaser ? "-mt-10" : "mt-2"}`}>
                    <Crown className="h-8 w-8 text-[#A78BFA] mx-auto mb-3" />
                    <h3 className="font-heading font-bold text-white text-xl">This is a Premium story</h3>
                    <p className="text-gray-400 mt-2 max-w-md mx-auto leading-relaxed">
                      Unlock the full analysis — plus every premium deep dive and a completely ad-free NewsLayer — from $9.99/month.
                    </p>
                    <div className="flex justify-center gap-3 mt-6">
                      <Link to="/premium" data-testid="paywall-upgrade-link"
                        className="sparkle-btn bg-[#7C3AED] text-white font-bold px-6 py-2.5 rounded-md hover:bg-[#6b2fd6] transition-colors">
                        Go Premium
                      </Link>
                      {!user && (
                        <Link to="/login" data-testid="paywall-login-link"
                          className="border border-white/20 text-white px-6 py-2.5 rounded-md hover:bg-white/5 transition-colors">
                          Sign in
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {dBody && !article.locked && (
                <>
                  <div className={collapsed ? "relative" : ""}>
                    <div className="mt-9" data-testid="article-body">
                      {narrowItems.map((it, i) => renderItem(it, i, false))}
                    </div>
                    {collapsed && (
                      <div aria-hidden data-testid="article-collapse-fade"
                        className="absolute bottom-0 inset-x-0 h-48 bg-gradient-to-t from-black via-black/80 to-transparent pointer-events-none" />
                    )}
                  </div>
                  {collapsed && (
                    <div className="mt-1 mb-4 flex justify-center">
                      <button data-testid="expand-story-button" onClick={expandStory}
                        className="group inline-flex items-center gap-2 rounded-full border border-[#00E5FF]/30 bg-[#0a1114] px-7 py-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-[#00E5FF] transition-colors hover:bg-[#00E5FF]/10 hover:border-[#00E5FF]/60">
                        Expand story
                        <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:translate-y-0.5" />
                      </button>
                    </div>
                  )}
                </>
              )}

              {!collapsed && !wideActive && <div ref={tailRef}>{tailContent}</div>}
            </div>

            {/* Tablet/mobile rail recomposition (§43-44): full-width stack after the article */}
            {!isDesktop && (
              <div className="mt-8 space-y-4" data-testid="mobile-rail-stack">
                {railModules}
              </div>
            )}

            {!collapsed && !wideActive && (
              <>
                <PrevNextNav nav={data.nav} />
                <AuthorEndCard authorName={article.author_name} contentType={article.content_type} />
              </>
            )}
          </div>

          {/* Right intelligence rail (§22-34) — LOCKED module order, measurable endpoint */}
          {isDesktop && (
            <aside className="hidden lg:block" data-testid="article-context-rail" aria-label="Story intelligence rail">
              <div ref={railInnerRef} className="space-y-4">
                {railModules}
              </div>
            </aside>
          )}
        </div>

        {/* Wide mode — the two-column grid ends at the true rail bottom; every
            remaining article block spans the full content container (one-column grid). */}
        {wideActive && (
          <div data-testid="article-wide-zone" className={tsClass}>
            {wideItems.map((it, idx) => renderItem(it, effFrom! + idx, true))}
            <div ref={tailRef}>
              {tailContent}
              <PrevNextNav nav={data.nav} />
              <AuthorEndCard authorName={article.author_name} contentType={article.content_type} />
            </div>
          </div>
        )}
      </article>

      {narrationOpen && !article.audio_url && (
        <NarrationPlayer slug={article.slug} title={dTitle} onClose={() => setNarrationOpen(false)} />
      )}

      {/* Keep Reading — More from NewsLayer (§38) — hidden while the story is collapsed */}
      {!collapsed && moreStories.length > 0 && (
        <section className="max-w-[1280px] mx-auto px-4 sm:px-6 pb-12" data-testid="related-stories-section">
          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[#A78BFA]">Keep Reading</p>
          <h2 className="font-heading font-bold text-white text-xl mt-1.5 mb-6" data-testid="related-stories-heading">
            {ctxHeading}
          </h2>
          {moreStories.length === 1 ? (
            <div className="lg:w-[52%]" data-testid="related-featured-wrap">
              <ArticleCard article={moreStories[0]} variant="featured" />
            </div>
          ) : (
            <div className={`grid grid-cols-1 gap-6 ${
              moreStories.length === 2 ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
              {moreStories.map((a: Article) => <ArticleCard key={a.id} article={a} />)}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
